import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import type { MovieFrame, Project, SavedMovie } from '../domain/model';
import {
  makeId,
  MAX_SAVED_MOVIE_BYTES,
  SAVE_HEADROOM_RATIO,
  savedMovieTitle,
  SPEEDS,
} from '../domain/model';
import type { ExportResult } from './exportMovie';
import { deliverMovie } from './deliverMovie';
import {
  listSavedMovies,
  markSavedMovieDeleted,
  purgeDeletedSavedMovies,
  putSavedMovie,
  restoreSavedMovie,
} from '../storage/movieLibrary';

export class SaveMovieError extends Error {
  constructor(
    message: string,
    readonly kind: 'too-big' | 'no-room' | 'failed' = 'failed',
  ) {
    super(message);
    this.name = 'SaveMovieError';
  }
}

/**
 * Best-effort space check before persisting. Browsers that cannot estimate
 * report 'unknown' and we proceed (the write itself will fail loudly on quota).
 */
export async function checkRoomFor(sizeBytes: number): Promise<'ok' | 'low' | 'unknown'> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (!estimate || estimate.quota === undefined || estimate.usage === undefined) return 'unknown';
    const headroom = estimate.quota - estimate.usage;
    return headroom >= sizeBytes * SAVE_HEADROOM_RATIO ? 'ok' : 'low';
  } catch {
    return 'unknown';
  }
}

/**
 * Pre-save decision for the post-export dialog: can this movie be kept?
 * 'too-big' = over the web-tier cap; 'low' = not enough room on the device.
 */
export async function canKeepMovie(sizeBytes: number): Promise<'ok' | 'too-big' | 'low' | 'unknown'> {
  if (!Capacitor.isNativePlatform() && sizeBytes > MAX_SAVED_MOVIE_BYTES) return 'too-big';
  return checkRoomFor(sizeBytes);
}

let persistenceRequested = false;

async function requestPersistence(): Promise<void> {
  if (persistenceRequested) return;
  persistenceRequested = true;
  // Advisory only — WebKit may ignore it. Never promised to the user.
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* best effort */
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function nativeMoviePath(id: string, extension: 'mp4' | 'webm'): string {
  return `movies/${id}.${extension}`;
}

/**
 * Persists a finished export into the local library.
 * Web tier: video Blob in IndexedDB (size-capped). Native tier: real file via
 * Capacitor Filesystem in Directory.Data, only metadata in IndexedDB.
 */
export async function saveFinishedMovie(
  project: Project,
  result: ExportResult,
  firstFrame: MovieFrame | undefined,
  frameCount: number,
): Promise<SavedMovie> {
  const sizeBytes = result.blob.size;
  const native = Capacitor.isNativePlatform();
  if (!native && sizeBytes > MAX_SAVED_MOVIE_BYTES) {
    throw new SaveMovieError('This movie is too big to keep in the app. You can still share or download it.', 'too-big');
  }
  if ((await checkRoomFor(sizeBytes)) === 'low') {
    throw new SaveMovieError('This device does not have enough room to keep this movie. Try deleting an old movie first.', 'no-room');
  }

  const fps = SPEEDS[project.speed].fps;
  const movie: SavedMovie = {
    id: makeId('saved'),
    projectId: project.id,
    title: savedMovieTitle(project),
    createdAt: Date.now(),
    mimeType: result.mimeType,
    extension: result.extension,
    durationMs: Math.round((frameCount / fps) * 1000),
    frameCount,
    speed: project.speed,
    width: firstFrame?.width ?? 0,
    height: firstFrame?.height ?? 0,
    sizeBytes,
  };
  if (firstFrame) movie.posterBlob = firstFrame.blob;

  if (native) {
    const written = await Filesystem.writeFile({
      path: nativeMoviePath(movie.id, movie.extension),
      data: await blobToBase64(result.blob),
      directory: Directory.Data,
      recursive: true,
    });
    movie.fileUri = written.uri;
  } else {
    movie.blob = result.blob;
  }

  try {
    await putSavedMovie(movie);
  } catch (error) {
    // Never leave an orphaned native file behind a failed metadata write.
    if (movie.fileUri) {
      await Filesystem.deleteFile({ path: nativeMoviePath(movie.id, movie.extension), directory: Directory.Data }).catch(() => undefined);
    }
    throw error;
  }
  await requestPersistence();
  return movie;
}

/** URL suitable for a <video> element. Web callers must revoke blob URLs. */
export function savedMoviePlaybackUrl(movie: SavedMovie): { url: string; revoke: boolean } | undefined {
  if (movie.fileUri) return { url: Capacitor.convertFileSrc(movie.fileUri), revoke: false };
  if (movie.blob) return { url: URL.createObjectURL(movie.blob), revoke: true };
  return undefined;
}

export async function shareSavedMovie(movie: SavedMovie): Promise<'shared' | 'downloaded'> {
  const filename = `${movie.title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '').slice(0, 48) || 'my-movie'}.${movie.extension}`;
  if (movie.fileUri) {
    await Share.share({
      title: 'My stop-motion movie',
      text: 'I made a stop-motion movie!',
      url: movie.fileUri,
      dialogTitle: 'Save or share your movie',
    });
    return 'shared';
  }
  if (!movie.blob) throw new SaveMovieError('This movie file is missing from this device.');
  return deliverMovie(movie.blob, filename);
}

export async function listFinishedMovies(): Promise<SavedMovie[]> {
  return listSavedMovies();
}

export async function deleteFinishedMovie(id: string): Promise<SavedMovie | undefined> {
  return markSavedMovieDeleted(id);
}

export async function undoDeleteFinishedMovie(id: string): Promise<SavedMovie | undefined> {
  return restoreSavedMovie(id);
}

/** Hard-deletes tombstoned movies and their native files. Call on library open. */
export async function purgeFinishedMovies(graceMs = 0): Promise<void> {
  const purged = await purgeDeletedSavedMovies(graceMs);
  for (const movie of purged) {
    if (movie.fileUri) {
      await Filesystem.deleteFile({ path: nativeMoviePath(movie.id, movie.extension), directory: Directory.Data }).catch(() => undefined);
    }
  }
}
